import React, { useEffect, useState } from 'react';
import { inventoryAPI } from '@/lib/api';
import { EntityLink } from '@/components/EntityLink';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { SearchableSelect } from '@/components/SearchableSelect';
import { 
  ArrowRightLeft, ClipboardCheck, ChevronLeft, ChevronRight, Package, Warehouse, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

export default function StockManagementPage() {
  const [activeTab, setActiveTab] = useState('stock');
  const [stock, setStock] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [reconciliations, setReconciliations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [godowns, setGodowns] = useState([]);

  // Pagination
  const [transferPage, setTransferPage] = useState(1);
  const [transferTotal, setTransferTotal] = useState(0);
  const [reconPage, setReconPage] = useState(1);
  const [reconTotal, setReconTotal] = useState(0);
  const limit = 20;

  // Filter
  const [godownFilter, setGodownFilter] = useState('');

  // Transfer Dialog
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferForm, setTransferForm] = useState({
    item_id: '',
    from_godown_id: '',
    to_godown_id: '',
    quantity: '',
    remarks: '',
  });
  const [transferring, setTransferring] = useState(false);

  // Reconcile Dialog
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [reconcileForm, setReconcileForm] = useState({
    item_id: '',
    godown_id: '',
    system_quantity: 0,
    physical_quantity: '',
    remarks: '',
  });
  const [reconciling, setReconciling] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (activeTab === 'stock') {
      loadStock();
    } else if (activeTab === 'transfers') {
      loadTransfers();
    } else if (activeTab === 'reconciliations') {
      loadReconciliations();
    }
  }, [activeTab, godownFilter, transferPage, reconPage]);

  const loadInitialData = async () => {
    try {
      const [itemsRes, godownsRes] = await Promise.all([
        inventoryAPI.listItems({ limit: 500 }),
        inventoryAPI.listGodowns(),
      ]);
      setItems(itemsRes.data.items || []);
      setGodowns(godownsRes.data || []);
    } catch { /* ignore */ }
  };

  const loadStock = async () => {
    setLoading(true);
    try {
      const params = {};
      if (godownFilter) params.godown_id = godownFilter;
      const res = await inventoryAPI.getStock(params);
      setStock(res.data || []);
    } catch { toast.error('Failed to load stock'); }
    finally { setLoading(false); }
  };

  const loadTransfers = async () => {
    setLoading(true);
    try {
      const res = await inventoryAPI.listTransfers({ page: transferPage, limit });
      setTransfers(res.data.transfers || []);
      setTransferTotal(res.data.total || 0);
    } catch { toast.error('Failed to load transfers'); }
    finally { setLoading(false); }
  };

  const loadReconciliations = async () => {
    setLoading(true);
    try {
      const params = { page: reconPage, limit };
      if (godownFilter) params.godown_id = godownFilter;
      const res = await inventoryAPI.listReconciliations(params);
      setReconciliations(res.data.reconciliations || []);
      setReconTotal(res.data.total || 0);
    } catch { toast.error('Failed to load reconciliations'); }
    finally { setLoading(false); }
  };

  // ================= TRANSFER =================

  const openTransfer = () => {
    setTransferForm({
      item_id: '',
      from_godown_id: '',
      to_godown_id: '',
      quantity: '',
      remarks: '',
    });
    setTransferOpen(true);
  };

  const getAvailableStock = (itemId, godownId) => {
    const rec = stock.find(s => s.item_id === itemId && s.godown_id === godownId);
    return rec ? rec.quantity : 0;
  };

  const handleTransfer = async () => {
    if (!transferForm.item_id || !transferForm.from_godown_id || !transferForm.to_godown_id) {
      toast.error('Select item and both godowns');
      return;
    }
    if (!transferForm.quantity || parseFloat(transferForm.quantity) <= 0) {
      toast.error('Enter valid quantity');
      return;
    }
    if (transferForm.from_godown_id === transferForm.to_godown_id) {
      toast.error('Source and destination must be different');
      return;
    }

    const available = getAvailableStock(transferForm.item_id, transferForm.from_godown_id);
    if (parseFloat(transferForm.quantity) > available) {
      toast.error(`Insufficient stock. Available: ${available}`);
      return;
    }

    setTransferring(true);
    try {
      await inventoryAPI.transferStock({
        item_id: transferForm.item_id,
        from_godown_id: transferForm.from_godown_id,
        to_godown_id: transferForm.to_godown_id,
        quantity: parseFloat(transferForm.quantity),
        remarks: transferForm.remarks,
      });
      toast.success('Stock transferred successfully');
      setTransferOpen(false);
      loadStock();
      loadTransfers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to transfer stock');
    } finally {
      setTransferring(false);
    }
  };

  // ================= RECONCILE =================

  const openReconcile = (stockItem = null) => {
    if (stockItem) {
      setReconcileForm({
        item_id: stockItem.item_id,
        godown_id: stockItem.godown_id,
        system_quantity: stockItem.quantity || 0,
        physical_quantity: '',
        remarks: '',
      });
    } else {
      setReconcileForm({
        item_id: '',
        godown_id: '',
        system_quantity: 0,
        physical_quantity: '',
        remarks: '',
      });
    }
    setReconcileOpen(true);
  };

  const onReconcileItemSelect = (itemId) => {
    setReconcileForm(f => {
      const newForm = { ...f, item_id: itemId };
      if (itemId && f.godown_id) {
        const sysQty = getAvailableStock(itemId, f.godown_id);
        newForm.system_quantity = sysQty;
      }
      return newForm;
    });
  };

  const onReconcileGodownSelect = (godownId) => {
    setReconcileForm(f => {
      const newForm = { ...f, godown_id: godownId };
      if (f.item_id && godownId) {
        const sysQty = getAvailableStock(f.item_id, godownId);
        newForm.system_quantity = sysQty;
      }
      return newForm;
    });
  };

  const handleReconcile = async () => {
    if (!reconcileForm.item_id || !reconcileForm.godown_id) {
      toast.error('Select item and godown');
      return;
    }
    if (reconcileForm.physical_quantity === '' || parseFloat(reconcileForm.physical_quantity) < 0) {
      toast.error('Enter valid physical quantity');
      return;
    }

    setReconciling(true);
    try {
      const res = await inventoryAPI.reconcileStock({
        item_id: reconcileForm.item_id,
        godown_id: reconcileForm.godown_id,
        system_quantity: reconcileForm.system_quantity,
        physical_quantity: parseFloat(reconcileForm.physical_quantity),
        remarks: reconcileForm.remarks,
      });
      
      if (res.data.variance === 0) {
        toast.info('No adjustment needed - stock already matches');
      } else {
        toast.success(`Stock reconciled. Variance: ${res.data.variance > 0 ? '+' : ''}${res.data.variance}`);
      }
      setReconcileOpen(false);
      loadStock();
      loadReconciliations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reconcile stock');
    } finally {
      setReconciling(false);
    }
  };

  const transferTotalPages = Math.ceil(transferTotal / limit);
  const reconTotalPages = Math.ceil(reconTotal / limit);

  const itemOptions = items.map(i => ({
    value: i.id,
    label: `${i.unique_id} - ${i.item_name}`,
    searchStr: `${i.unique_id} ${i.item_name}`,
  }));

  const godownOptions = godowns.map(g => ({
    value: g.id,
    label: `${g.code} - ${g.name}`,
  }));

  const getItemName = (itemId) => {
    const item = items.find(i => i.id === itemId);
    return item ? item.item_name : itemId;
  };

  const getGodownName = (godownId) => {
    const godown = godowns.find(g => g.id === godownId);
    return godown ? godown.name : godownId;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-foreground">Stock Management</h1>
          <p className="text-xs text-muted-foreground">Transfer and reconcile inventory (Admin only)</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => openReconcile()} data-testid="reconcile-btn">
            <ClipboardCheck className="w-3.5 h-3.5 mr-1" /> Reconcile Stock
          </Button>
          <Button size="sm" onClick={openTransfer} data-testid="transfer-btn">
            <ArrowRightLeft className="w-3.5 h-3.5 mr-1" /> Transfer Stock
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="stock" data-testid="tab-stock">Current Stock</TabsTrigger>
          <TabsTrigger value="transfers" data-testid="tab-transfers">Transfer History</TabsTrigger>
          <TabsTrigger value="reconciliations" data-testid="tab-reconciliations">Reconciliations</TabsTrigger>
        </TabsList>

        {/* Current Stock Tab */}
        <TabsContent value="stock">
          <Card>
            <CardHeader className="py-3 px-4 border-b">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Package className="w-4 h-4" /> Inventory Balances
                </CardTitle>
                <Select value={godownFilter || '_all'} onValueChange={v => setGodownFilter(v === '_all' ? '' : v)}>
                  <SelectTrigger className="w-48 h-7 text-xs" data-testid="godown-filter">
                    <SelectValue placeholder="All Godowns" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">All Godowns</SelectItem>
                    {godowns.map(g => (
                      <SelectItem key={g.id} value={g.id}>{g.code} - {g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-8 text-center text-muted-foreground text-sm">Loading...</div>
              ) : stock.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground text-sm">No stock found</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="p-2.5 text-left font-medium">Item</th>
                        <th className="p-2.5 text-left font-medium">Godown</th>
                        <th className="p-2.5 text-right font-medium">Quantity</th>
                        <th className="p-2.5 text-right font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {stock.map((s, idx) => (
                        <tr key={idx} className="hover:bg-muted/30">
                          <td className="p-2.5">
                            <p className="font-medium">{getItemName(s.item_id)}</p>
                          </td>
                          <td className="p-2.5">
                            <Badge variant="outline" className="text-[10px]">
                              <Warehouse className="w-3 h-3 mr-1" />
                              {getGodownName(s.godown_id)}
                            </Badge>
                          </td>
                          <td className="p-2.5 text-right font-mono">{s.quantity}</td>
                          <td className="p-2.5 text-right">
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="h-6 text-[10px] px-2"
                              onClick={() => openReconcile(s)}
                            >
                              <ClipboardCheck className="w-3 h-3 mr-0.5" /> Reconcile
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Transfer History Tab */}
        <TabsContent value="transfers">
          <Card>
            <CardHeader className="py-3 px-4 border-b">
              <CardTitle className="text-sm flex items-center gap-2">
                <ArrowRightLeft className="w-4 h-4" /> Stock Transfer History
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-8 text-center text-muted-foreground text-sm">Loading...</div>
              ) : transfers.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground text-sm">No transfers found</div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="p-2.5 text-left font-medium">Date</th>
                          <th className="p-2.5 text-left font-medium">Item</th>
                          <th className="p-2.5 text-left font-medium">From → To</th>
                          <th className="p-2.5 text-right font-medium">Qty</th>
                          <th className="p-2.5 text-left font-medium">By</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {transfers.map(t => (
                          <tr key={t.id} className="hover:bg-muted/30">
                            <td className="p-2.5">{t.created_at?.slice(0, 10)}</td>
                            <td className="p-2.5">
                              <p className="font-medium"><EntityLink type="item" id={t.item_id}>{t.item_name}</EntityLink></p>
                              <p className="text-[10px] text-muted-foreground"><EntityLink type="item" id={t.item_id} className="font-mono text-[10px] text-muted-foreground">{t.item_unique_id}</EntityLink></p>
                            </td>
                            <td className="p-2.5">
                              <span className="text-amber-600">{t.from_godown_name}</span>
                              <span className="mx-1">→</span>
                              <span className="text-green-600">{t.to_godown_name}</span>
                            </td>
                            <td className="p-2.5 text-right font-mono">{t.quantity}</td>
                            <td className="p-2.5 text-muted-foreground">{t.created_by_name}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {transferTotalPages > 1 && (
                    <div className="flex items-center justify-between p-3 border-t">
                      <span className="text-xs text-muted-foreground">Page {transferPage} of {transferTotalPages}</span>
                      <div className="flex gap-1">
                        <Button size="icon" variant="outline" className="h-7 w-7" disabled={transferPage === 1} onClick={() => setTransferPage(p => p - 1)}>
                          <ChevronLeft className="w-3 h-3" />
                        </Button>
                        <Button size="icon" variant="outline" className="h-7 w-7" disabled={transferPage === transferTotalPages} onClick={() => setTransferPage(p => p + 1)}>
                          <ChevronRight className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reconciliations Tab */}
        <TabsContent value="reconciliations">
          <Card>
            <CardHeader className="py-3 px-4 border-b">
              <CardTitle className="text-sm flex items-center gap-2">
                <ClipboardCheck className="w-4 h-4" /> Stock Reconciliation History
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-8 text-center text-muted-foreground text-sm">Loading...</div>
              ) : reconciliations.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground text-sm">No reconciliations found</div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="p-2.5 text-left font-medium">Date</th>
                          <th className="p-2.5 text-left font-medium">Item</th>
                          <th className="p-2.5 text-left font-medium">Godown</th>
                          <th className="p-2.5 text-right font-medium">System</th>
                          <th className="p-2.5 text-right font-medium">Physical</th>
                          <th className="p-2.5 text-right font-medium">Variance</th>
                          <th className="p-2.5 text-left font-medium">By</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {reconciliations.map(r => (
                          <tr key={r.id} className="hover:bg-muted/30">
                            <td className="p-2.5">{r.created_at?.slice(0, 10)}</td>
                            <td className="p-2.5">
                              <p className="font-medium">{r.item_name}</p>
                            </td>
                            <td className="p-2.5">{r.godown_name}</td>
                            <td className="p-2.5 text-right font-mono">{r.system_quantity}</td>
                            <td className="p-2.5 text-right font-mono">{r.physical_quantity}</td>
                            <td className="p-2.5 text-right">
                              <Badge 
                                variant={r.variance === 0 ? 'secondary' : r.variance > 0 ? 'default' : 'destructive'} 
                                className="text-[10px]"
                              >
                                {r.variance > 0 ? '+' : ''}{r.variance}
                              </Badge>
                            </td>
                            <td className="p-2.5 text-muted-foreground">{r.created_by_name}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {reconTotalPages > 1 && (
                    <div className="flex items-center justify-between p-3 border-t">
                      <span className="text-xs text-muted-foreground">Page {reconPage} of {reconTotalPages}</span>
                      <div className="flex gap-1">
                        <Button size="icon" variant="outline" className="h-7 w-7" disabled={reconPage === 1} onClick={() => setReconPage(p => p - 1)}>
                          <ChevronLeft className="w-3 h-3" />
                        </Button>
                        <Button size="icon" variant="outline" className="h-7 w-7" disabled={reconPage === reconTotalPages} onClick={() => setReconPage(p => p + 1)}>
                          <ChevronRight className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Transfer Dialog */}
      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <ArrowRightLeft className="w-4 h-4" /> Transfer Stock
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Item *</Label>
              <SearchableSelect
                options={itemOptions}
                value={transferForm.item_id}
                onSelect={v => setTransferForm(f => ({ ...f, item_id: v }))}
                placeholder="Search items..."
                emptyText="No items."
                testId="transfer-item"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">From Godown *</Label>
                <Select value={transferForm.from_godown_id} onValueChange={v => setTransferForm(f => ({ ...f, from_godown_id: v }))}>
                  <SelectTrigger className="h-8 text-xs" data-testid="transfer-from">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {godowns.map(g => (
                      <SelectItem key={g.id} value={g.id}>{g.code} - {g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {transferForm.item_id && transferForm.from_godown_id && (
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Available: {getAvailableStock(transferForm.item_id, transferForm.from_godown_id)}
                  </p>
                )}
              </div>
              <div>
                <Label className="text-xs">To Godown *</Label>
                <Select value={transferForm.to_godown_id} onValueChange={v => setTransferForm(f => ({ ...f, to_godown_id: v }))}>
                  <SelectTrigger className="h-8 text-xs" data-testid="transfer-to">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {godowns.filter(g => g.id !== transferForm.from_godown_id).map(g => (
                      <SelectItem key={g.id} value={g.id}>{g.code} - {g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Quantity *</Label>
              <Input 
                type="number"
                min="0.01"
                step="0.01"
                value={transferForm.quantity}
                onChange={e => setTransferForm(f => ({ ...f, quantity: e.target.value }))}
                className="h-8 text-xs"
                data-testid="transfer-qty"
              />
            </div>
            <div>
              <Label className="text-xs">Remarks</Label>
              <Textarea 
                value={transferForm.remarks}
                onChange={e => setTransferForm(f => ({ ...f, remarks: e.target.value }))}
                className="text-xs min-h-[60px]"
                placeholder="Optional notes..."
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" size="sm" onClick={() => setTransferOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={handleTransfer} disabled={transferring} data-testid="confirm-transfer-btn">
              {transferring ? 'Transferring...' : 'Transfer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reconcile Dialog */}
      <Dialog open={reconcileOpen} onOpenChange={setReconcileOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <ClipboardCheck className="w-4 h-4" /> Reconcile Stock
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Item *</Label>
              <SearchableSelect
                options={itemOptions}
                value={reconcileForm.item_id}
                onSelect={onReconcileItemSelect}
                placeholder="Search items..."
                emptyText="No items."
                testId="reconcile-item"
              />
            </div>
            <div>
              <Label className="text-xs">Godown *</Label>
              <Select value={reconcileForm.godown_id} onValueChange={onReconcileGodownSelect}>
                <SelectTrigger className="h-8 text-xs" data-testid="reconcile-godown">
                  <SelectValue placeholder="Select godown" />
                </SelectTrigger>
                <SelectContent>
                  {godowns.map(g => (
                    <SelectItem key={g.id} value={g.id}>{g.code} - {g.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {reconcileForm.item_id && reconcileForm.godown_id && (
              <div className="p-3 bg-muted/30 rounded-md">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground">System Quantity:</span>
                  <span className="text-sm font-bold">{reconcileForm.system_quantity}</span>
                </div>
              </div>
            )}

            <div>
              <Label className="text-xs">Physical Count (Actual) *</Label>
              <Input 
                type="number"
                min="0"
                step="0.01"
                value={reconcileForm.physical_quantity}
                onChange={e => setReconcileForm(f => ({ ...f, physical_quantity: e.target.value }))}
                className="h-8 text-xs"
                placeholder="Enter actual counted quantity"
                data-testid="reconcile-physical"
              />
            </div>

            {reconcileForm.physical_quantity !== '' && reconcileForm.system_quantity !== undefined && (
              <div className={`p-3 rounded-md flex items-center gap-2 ${
                parseFloat(reconcileForm.physical_quantity) !== reconcileForm.system_quantity 
                  ? 'bg-amber-50 border border-amber-200' 
                  : 'bg-green-50 border border-green-200'
              }`}>
                <AlertTriangle className={`w-4 h-4 ${
                  parseFloat(reconcileForm.physical_quantity) !== reconcileForm.system_quantity 
                    ? 'text-amber-600' 
                    : 'text-green-600'
                }`} />
                <div>
                  <p className="text-xs font-medium">
                    Variance: {(parseFloat(reconcileForm.physical_quantity || 0) - reconcileForm.system_quantity) > 0 ? '+' : ''}
                    {(parseFloat(reconcileForm.physical_quantity || 0) - reconcileForm.system_quantity).toFixed(2)}
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    {parseFloat(reconcileForm.physical_quantity) > reconcileForm.system_quantity 
                      ? 'Stock will be added' 
                      : parseFloat(reconcileForm.physical_quantity) < reconcileForm.system_quantity 
                        ? 'Stock will be reduced'
                        : 'No adjustment needed'}
                  </p>
                </div>
              </div>
            )}

            <div>
              <Label className="text-xs">Remarks</Label>
              <Textarea 
                value={reconcileForm.remarks}
                onChange={e => setReconcileForm(f => ({ ...f, remarks: e.target.value }))}
                className="text-xs min-h-[60px]"
                placeholder="Reason for variance..."
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" size="sm" onClick={() => setReconcileOpen(false)}>Cancel</Button>
            <Button size="sm" onClick={handleReconcile} disabled={reconciling} data-testid="confirm-reconcile-btn">
              {reconciling ? 'Reconciling...' : 'Reconcile'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
